// Port name gazetteer for display. Chile's source writes port names in Spanish;
// the UI always shows English. Display twin of route_ports.py's PORT_NAME_TO_CODE.
//
// Raw source spelling (upper case, no accents) → English display name.
// Keys must be 4+ chars and start/end with a letter or digit (the \b match
// relies on it). Names already English need no row.
export const PORT_NAME_TO_ENGLISH = {
  // --- North America ---
  FILADELFIA: 'Philadelphia',
  'NUEVA YORK': 'New York',
  'NUEVA ORLEANS': 'New Orleans',
  'NUEVA ESCOCIA': 'Nova Scotia',

  // --- Western Europe ---
  LONDRES: 'London',
  AMBERES: 'Antwerp',
  ROTERDAM: 'Rotterdam',
  FLESINGA: 'Vlissingen',
  HAMBURGO: 'Hamburg',
  BREMEN: 'Bremen',
  DUNKERQUE: 'Dunkirk',
  'EL HAVRE': 'Le Havre',
  BURDEOS: 'Bordeaux',
  MARSELLA: 'Marseille',
  LISBOA: 'Lisbon',

  // --- Northern Europe / Baltic ---
  COPENHAGUE: 'Copenhagen',
  GOTEMBURGO: 'Gothenburg',
  KLEIPEDA: 'Klaipeda', // FruitOnline misspelling
  TALLIN: 'Tallinn',
  'SAN PETERSBURGO': 'St Petersburg',
  MOSCU: 'Moscow',

  // --- Southern / Eastern Europe ---
  GENOVA: 'Genoa',
  LIORNA: 'Livorno',
  NAPOLES: 'Naples',
  VENECIA: 'Venice',
  'EL PIREO': 'Piraeus',
  SALONICA: 'Thessaloniki',
  ESTAMBUL: 'Istanbul',
  CONSTANZA: 'Constanta', // FruitOnline spelling
  NOVOROSISK: 'Novorossiysk',

  // --- Middle East / Asia ---
  YEDA: 'Jeddah',
  SINGAPUR: 'Singapore',
  YAKARTA: 'Jakarta',
  BOMBAY: 'Mumbai',
  CALCUTA: 'Kolkata',
  SHANGAI: 'Shanghai', // FruitOnline misspelling
  'HONG-KONG': 'Hong Kong',
  CANTON: 'Guangzhou',
  PEKIN: 'Beijing',
  TOKIO: 'Tokyo',
  PUSAN: 'Busan',

  // --- Africa ---
  'CIUDAD DEL CABO': 'Cape Town',
  ALEJANDRIA: 'Alexandria',
  'EL CAIRO': 'Cairo',
  TANGER: 'Tangier',

  // --- Latin America ---
  'CIUDAD DE PANAMA': 'Panama City',

  // --- Country / region tokens ---
  // These appear in the port column but are not ports (the pipeline's
  // NON_PORT_NAMES). They still have to read as English on the card.
  RUSIA: 'Russia',
  BIELORRUSIA: 'Belarus',
  ALEMANIA: 'Germany',
  ESPANA: 'Spain',
  FRANCIA: 'France',
  ITALIA: 'Italy',
  BELGICA: 'Belgium',
  'PAISES BAJOS': 'Netherlands',
  'REINO UNIDO': 'United Kingdom',
  GRECIA: 'Greece',
  TURQUIA: 'Turkey',
  SUECIA: 'Sweden',
  DINAMARCA: 'Denmark',
  NORUEGA: 'Norway',
  FINLANDIA: 'Finland',
  LETONIA: 'Latvia',
  LITUANIA: 'Lithuania',
  POLONIA: 'Poland',
  RUMANIA: 'Romania',
  UCRANIA: 'Ukraine',
  JAPON: 'Japan',
  'COREA DEL SUR': 'South Korea',
  'ARABIA SAUDITA': 'Saudi Arabia',
  'EMIRATOS ARABES UNIDOS': 'United Arab Emirates',
  SUDAFRICA: 'South Africa',
  MARRUECOS: 'Morocco',
  EGIPTO: 'Egypt',
  BRASIL: 'Brazil',
  'ESTADOS UNIDOS': 'United States',
};

/** Length-preserving accent fold, so "GÉNOVA" matches the key "GENOVA". */
const ACCENT_FOLD = {
  Á: 'A', À: 'A', Ä: 'A', Â: 'A', Ã: 'A', Å: 'A',
  É: 'E', È: 'E', Ë: 'E', Ê: 'E',
  Í: 'I', Ì: 'I', Ï: 'I', Î: 'I',
  Ó: 'O', Ò: 'O', Ö: 'O', Ô: 'O', Õ: 'O',
  Ú: 'U', Ù: 'U', Ü: 'U', Û: 'U',
  Ñ: 'N', Ç: 'C',
};

const FOLDABLE = /[ÁÀÄÂÃÅÉÈËÊÍÌÏÎÓÒÖÔÕÚÙÜÛÑÇ]/g;
const fold = (s) => s.replace(FOLDABLE, (ch) => ACCENT_FOLD[ch]);

/** Longest key first, so "SAN PETERSBURGO" wins over a hypothetical "SAN ...". */
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * One matcher per key, longest key first so "SAN PETERSBURGO" is tried before
 * any shorter key it contains. `\b` keeps a key from matching inside a longer
 * word — "CANTON" must not fire in "CANTONMENT".
 */
const MATCHERS = Object.entries(PORT_NAME_TO_ENGLISH)
  .filter(([key]) => key.length >= 4)
  .sort(([a], [b]) => b.length - a.length)
  .map(([key, english]) => ({ key, english, re: new RegExp(`\\b${escapeRe(key)}\\b`) }));

/** Source rows are upper case; keep them that way so mixed casing never appears on one card. */
const matchCase = (source, english) => (/[a-z]/.test(source) ? english : english.toUpperCase());

function translateSegment(segment) {
  const raw = segment.replace(/\s+/g, ' ').trim();
  if (!raw) return '';
  const upper = fold(raw.toUpperCase());
  const exact = PORT_NAME_TO_ENGLISH[upper];
  if (exact) return matchCase(raw, exact);
  for (const { key, english, re } of MATCHERS) {
    const at = upper.search(re);
    if (at < 0) continue;
    // Rewrite only the matched words, so "FILADELFIA, USA" keeps its tail.
    return raw.slice(0, at) + matchCase(raw, english) + raw.slice(at + key.length);
  }
  return raw;
}

export function toEnglishPortName(raw) {
  const value = String(raw ?? '').trim();
  if (!value) return value;
  return value.split(',').map(translateSegment).filter(Boolean).join(', ');
}

/** Port-name fields on a shipment row and on its nested stop/group/leg rows. */
const PORT_FIELDS = ['arrival_port', 'departure_port', 'all_arrival_ports', 'pol', 'pod', 'at_port'];

/** voyage_stops / vessel_legs / ship_changes arrive as an array or a JSON string. */
function asRows(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

const NESTED_ROW_FIELDS = ['voyage_stops', 'tracked_groups', 'vessel_legs', 'ship_changes'];

function translateRow(row) {
  if (!row || typeof row !== 'object') return row;
  const out = { ...row };
  PORT_FIELDS.forEach((field) => {
    const value = out[field];
    if (typeof value !== 'string' || !value) return;
    const english = toEnglishPortName(value);
    if (english === value) return;
    // Keep the source spelling searchable — container search matches on it.
    out[`${field}_source`] = value;
    out[field] = english;
  });
  NESTED_ROW_FIELDS.forEach((field) => {
    const rows = asRows(out[field]);
    if (rows) out[field] = rows.map(translateRow);
  });
  return out;
}

export const withEnglishPortNames = (shipment) => translateRow(shipment);

export function withEnglishPorts(payload) {
  const data = payload?.data;
  if (!data || typeof data !== 'object') return payload;
  const next = { ...data };
  if (Array.isArray(next.shipments)) next.shipments = next.shipments.map(withEnglishPortNames);
  if (next.shipment && typeof next.shipment === 'object') {
    next.shipment = withEnglishPortNames(next.shipment);
  }
  if (Array.isArray(next.flights)) next.flights = next.flights.map(withEnglishPortNames);
  if (Array.isArray(next.trucks)) next.trucks = next.trucks.map(withEnglishPortNames);
  if (Array.isArray(next.history)) next.history = next.history.map(withEnglishPortNames);
  return { ...payload, data: next };
}
